import React, { useState, useEffect } from 'react';
import { messageApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  MessageSquare, Send, Plus, Mail, MailOpen, Search,
  User, Clock
} from 'lucide-react';

export default function MessagesPage() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [composeOpen, setComposeOpen] = useState(false);
  const [newMessage, setNewMessage] = useState({ recipient_id: '', subject: '', content: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    setLoading(true);
    try {
      const response = await messageApi.inbox();
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMessage = async (msg) => {
    setSelectedMessage(msg);
    if (!msg.read) {
      try {
        await messageApi.markRead(msg.id);
        setMessages(messages.map(m => 
          m.id === msg.id ? { ...m, read: true } : m
        ));
      } catch (error) {
        console.error('Failed to mark message as read:', error);
      }
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.subject.trim() || !newMessage.content.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSending(true);
    try {
      await messageApi.send({
        recipient_id: newMessage.recipient_id || user.id, // Demo: send to self if no recipient
        subject: newMessage.subject,
        content: newMessage.content
      });
      toast.success('Message sent');
      setComposeOpen(false);
      setNewMessage({ recipient_id: '', subject: '', content: '' });
      loadMessages();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const filteredMessages = messages.filter(msg =>
    msg.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
    msg.sender_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const unreadCount = messages.filter(m => !m.read).length;

  return (
    <div className="space-y-6" data-testid="messages-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Messages
          </h1>
          <p className="text-[#64748B] mt-1">
            {unreadCount > 0 ? `${unreadCount} unread messages` : 'No unread messages'}
          </p>
        </div>
        <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
          <DialogTrigger asChild>
            <Button className="careiq-btn-primary flex items-center gap-2" data-testid="compose-message-btn">
              <Plus className="w-4 h-4" />
              Compose
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>New Message</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="careiq-label">Subject</label>
                <Input
                  placeholder="Enter subject"
                  value={newMessage.subject}
                  onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })}
                  className="careiq-input"
                  data-testid="message-subject-input"
                />
              </div>
              <div>
                <label className="careiq-label">Message</label>
                <Textarea
                  placeholder="Type your message..."
                  value={newMessage.content}
                  onChange={(e) => setNewMessage({ ...newMessage, content: e.target.value })}
                  className="careiq-input min-h-[150px]"
                  data-testid="message-content-input"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setComposeOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  className="careiq-btn-primary" 
                  onClick={handleSendMessage}
                  disabled={sending}
                  data-testid="send-message-btn"
                >
                  {sending ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Message List */}
        <Card className="lg:col-span-1 careiq-card">
          <CardHeader className="pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
              <Input
                placeholder="Search messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="careiq-input pl-10"
                data-testid="message-search-input"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : filteredMessages.length > 0 ? (
                <div className="divide-y divide-[#E2E8F0]">
                  {filteredMessages.map((msg) => (
                    <button
                      key={msg.id}
                      onClick={() => handleSelectMessage(msg)}
                      className={`w-full p-4 text-left hover:bg-[#F8FAFC] transition-colors ${
                        selectedMessage?.id === msg.id ? 'bg-[#0E7490]/5' : ''
                      } ${!msg.read ? 'bg-[#F8FAFC]' : ''}`}
                      data-testid={`message-item-${msg.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-[#0E7490]/10 rounded-full flex items-center justify-center flex-shrink-0">
                          {msg.read ? (
                            <MailOpen className="w-4 h-4 text-[#64748B]" />
                          ) : (
                            <Mail className="w-4 h-4 text-[#0E7490]" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`font-medium text-sm truncate ${!msg.read ? 'text-[#0A2342]' : 'text-[#64748B]'}`}>
                            {msg.sender_name}
                          </div>
                          <div className={`text-sm truncate ${!msg.read ? 'text-[#0A2342] font-medium' : 'text-[#64748B]'}`}>
                            {msg.subject}
                          </div>
                          <div className="text-xs text-[#94A3B8] mt-1 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(msg.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        {!msg.read && (
                          <div className="w-2 h-2 bg-[#0E7490] rounded-full flex-shrink-0" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-[#64748B]">
                  <MessageSquare className="w-10 h-10 mx-auto mb-2 text-[#94A3B8]" />
                  <p className="text-sm">No messages found</p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Message Detail */}
        <Card className="lg:col-span-2 careiq-card">
          <CardContent className="p-6 min-h-[500px]">
            {selectedMessage ? (
              <div className="space-y-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-[#0A2342]">{selectedMessage.subject}</h2>
                    <div className="flex items-center gap-2 mt-2 text-sm text-[#64748B]">
                      <div className="w-8 h-8 bg-[#0E7490] rounded-full flex items-center justify-center text-white text-sm">
                        {selectedMessage.sender_name.charAt(0)}
                      </div>
                      <span className="font-medium text-[#0A2342]">{selectedMessage.sender_name}</span>
                      <span>•</span>
                      <span>{new Date(selectedMessage.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <Button variant="outline" className="careiq-btn-outline">
                    <Send className="w-4 h-4 mr-2" />
                    Reply
                  </Button>
                </div>
                <div className="border-t border-[#E2E8F0] pt-6">
                  <p className="text-[#334155] whitespace-pre-wrap leading-relaxed">
                    {selectedMessage.preview}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-[#64748B]">
                <MessageSquare className="w-16 h-16 text-[#94A3B8] mb-4" />
                <p className="text-lg font-medium text-[#0A2342]">Select a message</p>
                <p className="text-sm">Choose a message from the list to view its contents</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
